import { type ChangeEvent, type FC } from 'react'
import { Button, GenericError, PieChart } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  type EnrollmentUserInvestmentRiskToleranceOutput,
  type EnrollmentFixedIncomeRecommendationOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { type GQLError } from '@egw/clients'
import { HowItWorks } from '../how-it-works/how-it-works'
import { SectorSliders } from '../sector-sliders/sector-sliders'
import { TotalDistribution } from '../total-distribution/total-distribution'
import { type RiskTolerance } from '../modal-risk-tolerance/modal-risk-tolerance'

interface FixedIncomeProps {
  riskTolerance?: EnrollmentUserInvestmentRiskToleranceOutput | RiskTolerance
  sliderData?: EnrollmentFixedIncomeRecommendationOutput[] | undefined
  isLoading?: boolean
  isSubmitting?: boolean
  error?: GQLError | null
  submitError?: GQLError | null
  handleContinueClick: () => void
  onChangeSliders: (_index: number, _event: ChangeEvent<HTMLInputElement>) => void
  withContainer?: boolean
  isShowCurrentValue?: boolean
  experience?: string
}

export const FixedIncome: FC<FixedIncomeProps> = ({
  riskTolerance,
  sliderData,
  isLoading = false,
  isSubmitting = false,
  error = undefined,
  submitError = undefined,
  handleContinueClick,
  onChangeSliders,
  withContainer = true,
  isShowCurrentValue = false,
  experience,
}) => {
  const { t } = useTranslation('features-enrollment')

  const total = sliderData?.reduce(
    (sum, cur) => sum + (cur.currentAllocationPercent ?? cur.recommendedRange.recommendedPercent),
    0,
  )
  const isTotalError = total !== 100

  const isOutsideRange = sliderData?.some(
    (slider) =>
      (slider.currentAllocationPercent ?? slider.recommendedRange.recommendedPercent) <
        slider.recommendedRange.allowedMinPercent ||
      (slider.currentAllocationPercent ?? slider.recommendedRange.recommendedPercent) >
        slider.recommendedRange.allowedMaxPercent,
  )

  return (
    <div className="flex flex-col w-full">
      <div className={`${withContainer ? 'egw-container-enroll' : ''} h-full flex flex-col`}>
        {/* Loading initial data to make sure the user has access to this screen and retrieve data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <div className="flex justify-between w-full">
              <div className="flex flex-col justify-center">
                <p className="typo-primary-2xl-light text-text-header">{t('investments.fixed-income.title')}</p>
                <p className="text-text-meta typo-primary-sm-regular mt-xs">
                  {riskTolerance?.allocation.fixedIncomePercent}% {t('common.of_personal_portfolio')}
                </p>
              </div>
              {riskTolerance ? (
                <PieChart
                  segmentOne={riskTolerance.allocation.equityPercent}
                  segmentTwo={riskTolerance.allocation.fixedIncomePercent}
                  segmentThree={riskTolerance.allocation.cashEquivalentPercent}
                  highlightedSegment={2}
                  showPercentage={false}
                  showAdditionalOverlays
                  className="w-[120px] h-[100px]"
                />
              ) : null}
            </div>

            <HowItWorks
              linkText={t('common.how_it_works_title')}
              className="typo-primary-sm-semibold mt-sm"
              expandedContent={
                <div className="flex flex-col gap-3 typo-primary-sm-light text-tex-body">
                  <p>{t('common.how_it_works.paragraph_1')}</p>
                  <p>{t('common.how_it_works.paragraph_2')}</p>
                  <ul className="list-disc list-inside flex flex-col gap-3">
                    <li>{t('common.how_it_works.paragraph_3')}</li>
                    <li>{t('common.how_it_works.paragraph_4')}</li>
                  </ul>
                  <p>{t('common.how_it_works.paragraph_5')}</p>
                </div>
              }
            />
            <HowItWorks
              linkText={t('investments.fixed-income.about_title')}
              className="mt-2xs typo-primary-sm-semibold"
              expandedContent={
                <div className="flex flex-col typo-primary-sm-light text-tex-body">
                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.fixed-income.about.government_bonds_title')}
                  </p>
                  <p className="mb-xs">{t('investments.fixed-income.about.government_bonds')}</p>
                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.fixed-income.about.high_yield_bonds_title')}
                  </p>
                  <p className="mb-xs">{t('investments.fixed-income.about.high_yield_bonds')}</p>
                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.fixed-income.about.corporate_bonds_title')}
                  </p>
                  <p className="mb-xs">{t('investments.fixed-income.about.corporate_bonds')}</p>
                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.fixed-income.about.municipal_bonds_title')}
                  </p>
                  <p className="mb-xs">{t('investments.fixed-income.about.municipal_bonds')}</p>
                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.fixed-income.about.international_bonds_title')}
                  </p>
                  <p className="mb-xs">{t('investments.fixed-income.about.international_bonds')}</p>
                </div>
              }
            />

            {/** Slider Key */}
            <div className="flex flex-row gap-8 mt-2xl mb-sm text-text-meta typo-primary-sm-regular">
              <div className="flex flex-row gap-3">
                <div
                  data-testid="range-slider-key-recommended"
                  className="self-center size-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-[#4AA8F9]"
                />
                {t('common.recommended')}
              </div>
              {isShowCurrentValue ? (
                <div className="flex flex-row gap-2">
                  <div
                    data-testid="range-slider-key-original"
                    className="self-center w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-meta"
                  />
                  {t('common.current')}
                </div>
              ) : null}
            </div>

            {sliderData ? (
              <div role="group" aria-label={t('investments.equity.title')}>
                <SectorSliders
                  isShowCurrentValue={isShowCurrentValue}
                  sliderData={sliderData}
                  testId="equity-sliders"
                  onChange={onChangeSliders}
                  experience={experience}
                />
              </div>
            ) : null}
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>

      {!isLoading && !error && !submitError ? (
        <div className="bg-white sticky bottom-0 left-0 right-0 z-10">
          <div className={`${withContainer ? 'egw-container-enroll' : ''} !pt-md flex flex-col `}>
            {total ? <TotalDistribution total={total} isTotalError={isTotalError} /> : null}

            <Button
              type="submit"
              className="w-full md:w-min mt-md"
              isDisabled={isTotalError || isOutsideRange}
              isLoading={isSubmitting}
              testId="enrollment-continue-button"
              onClick={() => {
                handleContinueClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
